import React, { useCallback, useEffect, useMemo, useState } from "react";
import { documentService } from "../../services/documentService";
import { PdfFileViewer } from "./PdfFileViewer";
import { MailFileViewer } from "./MailFileViewer";
import { PdfDocumentView } from "./PdfDocumentView";
import {
    canOpenDocumentInViewer,
    canPreviewInline,
    isMailFileLike,
    isOfficeLike,
    isPdfLike,
    isPreviewImage,
    isTextLike,
    oeffnenArt,
} from "./documentKind";
import { BildAnsicht } from "./BildAnsicht";
import { OfficeDocumentView } from "./OfficeDocumentView";
import { baueMailDateiAktionen, type MailDateiKontext } from "./mailDateiAntworten";
import { useWindowManagerOptional } from "../windowManager/WindowManagerContext";
import { useMailContextOptional } from "../../context/MailContext";

// Re-Export, damit bestehende Importpfade gültig bleiben; die Regeln selbst
// leben in `documentKind.ts` (eine Regel, eine Quelle).
export { isPdfLike, isTextLike, isPreviewImage, canPreviewInline, isMailFileLike, isOfficeLike, canOpenDocumentInViewer };

/**
 * Fullpage-Inhalt des Datei-Fensters: PDF → Viewer, Text → <pre>, Bild → img.
 *
 * Die Datei kommt über `load()` — dadurch funktioniert derselbe Viewer für
 * KMVS-Dokumente (`documentService.fetchBlob`) und für Mail-Anhänge, die keine
 * Dokument-Id haben.
 */
export const FileViewerContent: React.FC<{
    load: () => Promise<Blob>;
    fileName: string;
    mimeType?: string | null;
    searchTerm?: string;
    /** Fallback-Aktion, wenn der Typ nicht inline darstellbar ist. */
    onDownload?: () => void;
}> = ({ load, fileName, mimeType, searchTerm, onDownload }) => {
    const [url, setUrl] = useState<string | null>(null);
    const [text, setText] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Ohne Dokument-Id: Mail-Dateien und Office gibt es hier nicht — die Weiche
    // `oeffnenArt` liefert dann `download`.
    const art = oeffnenArt(fileName, mimeType);
    const darstellbar = art === "pdf" || art === "bild" || art === "text";

    useEffect(() => {
        let alive = true;
        let objectUrl: string | null = null;
        if (!darstellbar) return;
        load()
            .then(async (blob) => {
                if (!alive) return;
                if (art === "text") {
                    setText(await blob.text());
                } else {
                    objectUrl = URL.createObjectURL(blob);
                    setUrl(objectUrl);
                }
            })
            .catch(() => alive && setError("Datei konnte nicht geladen werden."));
        return () => {
            alive = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // `load` ist je Aufruf eine neue Closure — die Identität der Datei hängt
        // an Name/Typ, nicht an der Funktionsreferenz.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileName, mimeType]);

    // Nicht darstellbar (docx, xlsx, zip, tiff …): ehrlicher Hinweis statt einer
    // leeren Bildfläche.
    if (!darstellbar) {
        return (
            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
                <div>Für diesen Dateityp gibt es keine Vorschau.</div>
                {onDownload && (
                    <button type="button" className="kvp-rd-btn" onClick={onDownload}>
                        Herunterladen
                    </button>
                )}
            </div>
        );
    }

    if (error) return <div style={{ padding: 24 }}>{error}</div>;
    if (text != null) {
        return <pre style={{ height: "100%", overflow: "auto", margin: 0, padding: 16, whiteSpace: "pre-wrap" }}>{text}</pre>;
    }
    if (!url) return <div style={{ padding: 24 }}>Wird geladen…</div>;
    if (art === "pdf") return <PdfFileViewer url={url} searchTerm={searchTerm} />;
    return <BildAnsicht url={url} fileName={fileName} onDownload={onDownload} />;
};

/** KMVS-Dokument im Viewer — dünner Wrapper um {@link FileViewerContent}. */
export const DocumentViewerContent: React.FC<{
    docId: number;
    fileName: string;
    mimeType?: string | null;
    searchTerm?: string;
    /** Nach dem Umbenennen im PDF-Viewer — z. B. für den Fenstertitel. */
    onRenamed?: (fileName: string) => void;
    /**
     * Office-Datei: beim Öffnen gleich „Im Web / Desktop“ anbieten. Aus, wenn
     * der Nutzer ausdrücklich Fassungen und Sicherungsstatus sehen will — die
     * Zielwahl hatte er dann gerade vor sich.
     */
    officeZielwahl?: boolean;
    /**
     * T-247: Vorgang/Kunde/Größe des Dokuments — bei Mail-Dateien kommen die
     * Antwort und das Weiterleiten damit an den Vorgang, in dem sie liegen.
     */
    mailKontext?: MailDateiKontext;
}> = ({ docId, fileName, mimeType, searchTerm, onRenamed, officeZielwahl = true, mailKontext }) => {
    const load = useCallback(() => documentService.fetchBlob(docId), [docId]);
    const wm = useWindowManagerOptional();
    const mailCtx = useMailContextOptional();
    const art = oeffnenArt(fileName, mimeType, { mitDokumentId: true });
    const istMail = art === "mail";
    const mailAktionen = useMemo(() => {
        if (!istMail || !wm) return undefined;
        const eigene = [
            ...(mailCtx?.mailboxes ?? []).map((m) => m.mailboxAddress),
            ...(mailCtx?.currentMailbox ? [mailCtx.currentMailbox] : []),
        ];
        return baueMailDateiAktionen({
            docId,
            fileName,
            mimeType,
            ...(mailKontext ? { kontext: mailKontext } : {}),
            eigeneAdressen: eigene,
            // Dynamisch: das Verfassen-Fenster zieht den halben Mail-Bereich
            // nach sich — das Dokumentfenster braucht ihn erst beim Klick.
            oeffne: (params) =>
                void import("../mail/ComposeMailWindow").then((m) => m.openComposeWindow(wm.openWindow, params)),
        });
    }, [istMail, wm, mailCtx?.mailboxes, mailCtx?.currentMailbox, docId, fileName, mimeType, mailKontext]);
    const onDownload = useCallback(() => void documentService.openSignedDownload(docId), [docId]);

    // Mail-Dateien werden serverseitig geparst (Kopfdaten, sanitisiertes HTML,
    // Anhänge) — der Blob-Weg unten könnte damit nichts anfangen. Die Weiche steht
    // bewusst VOR `FileViewerContent`, damit gar kein Blob geladen wird.
    if (istMail) {
        return <MailFileViewer docId={docId} {...(mailAktionen ? { aktionen: mailAktionen } : {})} />;
    }

    // PDFs eines KMVS-Dokuments bekommen den Bearbeiten-Umschalter. Der generische
    // Blob-Pfad unten bleibt für alles ohne Dokument-Id (z. B. Mail-Anhänge).
    // Bilder (JPG, PNG, WebP, GIF, BMP) gehen denselben Weg (User 04.10.2026):
    // dieselbe Leiste, derselbe Editor — das Bild bleibt beim Speichern ein Bild
    // oder wird auf Wunsch zum PDF. SVG/TIFF bleiben beim Blob-Pfad unten.
    // Ob es wirklich ein Bild ist, entscheidet `PdfDocumentView` an den Bytes:
    // nur echte PDFs (und in ein PDF gehüllte Bilder) gehen an pdf.js, alles
    // andere zeigt die Bildvorschau.
    if (art === "pdf" || art === "bild-editor") {
        return (
            <PdfDocumentView
                docId={docId}
                fileName={fileName}
                mimeType={mimeType}
                searchTerm={searchTerm}
                onRenamed={onRenamed}
            />
        );
    }

    // Word/Excel/PowerPoint: der Document Server holt die Datei selbst ab, hier
    // wird nichts geladen. Die Weiche steht — wie bei den Mail-Dateien — VOR
    // `FileViewerContent`, damit gar kein Blob angefordert wird.
    //
    // Kein `enabled`-Vorbehalt an dieser Stelle: wer bis hierher kommt, wurde
    // von `useOpenDocument` geschickt, und das fragt vorher. Ein zweiter,
    // asynchroner Check würde nur ein Flackern erzeugen.
    if (art === "office") {
        return <OfficeDocumentView docId={docId} fileName={fileName} chooseTargetOnOpen={officeZielwahl} />;
    }

    return (
        <FileViewerContent
            load={load}
            fileName={fileName}
            mimeType={mimeType}
            searchTerm={searchTerm}
            onDownload={onDownload}
        />
    );
};
